import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
  AriaRole,
} from "react";
import type { NavProps, NavIdType } from "./types";
import type { NavItemProps } from "./components/NavItem/types";
import { randomIdString } from "shared/src/utils/randomIdString";
import { itemConstructor } from "./js/itemConstructor";
import { Carousel } from "../Carousel/Carousel";
import cx from "components/src/utils/cx";
import { createId } from "./js/itemConstructor";
import { templateCols } from "../Grid/js/gridClasses";
import omit from "lodash-es/omit";
import { cssColor } from "shared/src/utils/cssUtils";

/**
 * CUI Nav component
 * @param id string
 * @param textLimit undefined | "character" | "line"
 * @returns
 */

const defaultSkipLink = { label: "Pill Menu", anchorTag: "#" };

const bottomBorderClasses =
  "cui-border-b-1 cui-border-t-0 cui-border-x-0 cui-border-solid cui-border-fg-minimal";

const navSizeHeightMap = {
  xs: 34,
  sm: 42,
  md: 52,
  lg: 62,
};

export const Nav = ({
  id,
  type = "default",
  variant = "tab",
  items = [],
  itemSize = "md",
  size = "sm",
  textLimitType,
  textLimit,
  initialActive = 0,
  activateItem,
  overlay = false,
  skipLink = defaultSkipLink,
  tabbable = true,
  animation = true,
  equalWidth,
  mode,
  followSelected = true,
  simple,
  boldText,
  fullWidth,
  showBottomBorder = true,
  forceMaxWidth,
  className = "",
  skeleton,
  bgColor,
  onActiveItem,
  ...rest
}: NavProps) => {
  const [active, setActive] = useState<NavIdType | undefined>(initialActive);
  const [leftFade, showFadeLeft] = useState(false);
  const [carouselActiveIndex, setCarouselActiveIndex] = useState(initialActive);

  const idRef = useRef(randomIdString());
  const _id = useMemo(() => id ?? `cui-nav-${idRef.current}`, [id]);
  const _showBottomBorder = showBottomBorder && variant === "tab" && type === "default";

  /**
   * Item indexes for lookup
   */

  const { itemsObjByIndexMap = {}, itemsObjByIdMap = {} } = useMemo(() => {
    if (Array.isArray(items) && !items.length) return {};
    const itemsObjByIndexMap = (items as NavItemProps[]).reduce(
      (acc, item, index) => {
        let localId = createId(_id, item, index);
        acc[localId] = index;
        return acc;
      },
      {} as { [key: string]: number }
    );
    const itemsObjByIdMap = Object.fromEntries(
      Object.entries(itemsObjByIndexMap).map((entry) => entry.reverse())
    );
    return { itemsObjByIndexMap, itemsObjByIdMap };
  }, [items, _id]);

  /**
   * Computed
   */

  const { _items } = itemConstructor({
    id: _id,
    type,
    items,
    size,
    textLimitType,
    textLimit,
    animation,
    active,
    mode,
    setActive,
    onActiveItem,
    bgColor,
    variant,
    boldText,
    skeleton,
    itemsObjByIndexMap,
    itemsObjByIdMap,
  });

  let _active =
    typeof active === "string" && /[a-zA-Z]+/.test(active)
      ? itemsObjByIndexMap[active]
      : Number(active);
  const _slideTo = followSelected ? { slideTo: _active } : undefined;

  /**
   * Hooks and Callbacks
   */

  const handleItemChange = useCallback((activeIndex: number) => {
    setCarouselActiveIndex(activeIndex);
  }, []);

  useEffect(() => {
    if (onActiveItem && active !== undefined) {
      onActiveItem(active);
    }
    if (_active === carouselActiveIndex) {
      showFadeLeft(false);
    } else {
      showFadeLeft(true);
    }
  }, [_active, active, carouselActiveIndex]);

  useEffect(() => {
    if (
      typeof items[0] === "object" &&
      activateItem !== undefined &&
      (typeof activateItem === "string" || typeof activateItem === "number")
    ) {
      setActive(
        itemsObjByIndexMap[activateItem] ?? itemsObjByIdMap[activateItem]
      );
    }
    if (
      typeof items[0] === "string" &&
      activateItem !== undefined &&
      typeof activateItem === "number"
    ) {
      setActive(activateItem);
    }
  }, [activateItem]);

  if (simple)
    return (
      <SimpleContainer
        className={className}
        showBottomBorder={_showBottomBorder}
        fullWidth={fullWidth}
        variant={variant}
        itemsLength={items?.length}
        equalWidth={equalWidth}
        forceMaxWidth={forceMaxWidth}
        size={size}
        type={type}
        {...rest}
      >
        {_items}
      </SimpleContainer>
    );

  return (
    <Carousel
      as="nav"
      className={cx({
        [bottomBorderClasses]: _showBottomBorder,
        [cssColor(bgColor)]: !!bgColor,
        [className]: true,
      })}
      bottomPadding={_showBottomBorder ? "0px" : undefined}
      topPadding={variant === "stacked" ? "8px" : undefined}
      controlSize={size}
      overlay={overlay}
      showPagination={false}
      centerSlides={false}
      onItemChange={handleItemChange}
      showFadeLeft={leftFade}
      data-cy="cui__nav"
      skipLink={skipLink}
      tabbable={tabbable}
      role={"menu" as AriaRole}
      spaceBetween={0}
      {..._slideTo}
      {...rest}
      style={{
        background: cssColor(bgColor)
      }}
    >
      {_items}
    </Carousel>
  );
};

const SimpleContainer = ({
  children,
  variant,
  showBottomBorder,
  fullWidth,
  equalWidth = false,
  className = "",
  itemsLength,
  size,
  type,
  forceMaxWidth,
  ...rest
}) => {
  const _rest = omit(rest, ["cssMode", "showControls"])
  return (
    <div
      className={cx(
        {
          "cui-relative": forceMaxWidth,
        },
        "cui__nav-simple-wrapper cui-w-full cui-max-w-full cui-overflow-x-auto cui-no-scrollbar"
      )}
      style={forceMaxWidth ? { height: navSizeHeightMap[size] } : {}}
    >
      <nav
        {..._rest}
        className={cx(
          {
            "cui-inline-flex": !equalWidth && !fullWidth,
            "cui-flex": !equalWidth && fullWidth,
            [`cui-grid ${templateCols[itemsLength]}`]: equalWidth,
            [bottomBorderClasses]: showBottomBorder && type === "default",
            "cui-relative": !forceMaxWidth,
            "cui-absolute cui-top-0 cui-left-0": forceMaxWidth,
            [className]: true,
          },
          "cui-space-x-2"
        )}
        role="tablist"
      >
        {children}
      </nav>
    </div>
  );
};
